import axios from 'axios'
import { ElMessage } from 'element-plus'
import { clearAuth, getToken } from '@/utils/auth'

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL || '/api'

const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 300000,  // 5分钟超时，用于长时间排考操作
    headers: {
        'Content-Type': 'application/json'
    }
})

// Request interceptor
api.interceptors.request.use((config) => {
    // 1. Check if the URL contains NaN, null, or undefined as a path segment
    const invalidPathRegex = /\/(NaN|null|undefined)(\/|\?|$)/i
    if (invalidPathRegex.test(config.url || '')) {
        const err = new Error('请求包含无效的参数')
        err.silent = true
        return Promise.reject(err)
    }

    // 2. Clean up or reject NaN/null/undefined query params
    if (config.params) {
        for (const key in config.params) {
            const val = config.params[key]
            if (val === 'NaN' || val === 'undefined' || val === 'null' || val === null || val === undefined || (typeof val === 'number' && isNaN(val))) {
                if (val === null || val === undefined) {
                    delete config.params[key]
                } else {
                    const err = new Error(`请求查询参数 ${key} 包含无效的值(${val})`)
                    err.silent = true
                    return Promise.reject(err)
                }
            }
        }
    }

    // 3. Clean up or reject NaN/null/undefined body params
    if (config.data && typeof config.data === 'object') {
        for (const key in config.data) {
            const val = config.data[key]
            if (val === 'NaN' || val === 'undefined' || val === 'null' || val === null || val === undefined || (typeof val === 'number' && isNaN(val))) {
                if (val === null || val === undefined) {
                    delete config.data[key]
                } else {
                    const err = new Error(`请求体参数 ${key} 包含无效的值(${val})`)
                    err.silent = true
                    return Promise.reject(err)
                }
            }
        }
    }

    const token = getToken()
    if (token) {
        config.headers = config.headers || {}
        config.headers.Authorization = `Bearer ${token}`
    }
    return config
})

// Response interceptor
api.interceptors.response.use(
    response => {
        // 对于 blob 类型（文件下载），直接返回 data
        if (response.config.responseType === 'blob') {
            return response.data
        }
        return response.data
    },
    error => {
        if (error.silent) {
            return Promise.reject(error)
        }
        const isLoginRequest = (error.config?.url || '').includes('/auth/login')
        if (error.response?.status === 401 && !isLoginRequest) {
            clearAuth()
            if (window.location.pathname !== '/login') {
                window.location.href = '/login'
            }
            ElMessage.error('登录已过期，请重新登录')
            return Promise.reject(error)
        }
        let message = error.response?.data?.detail || error.message || '请求失败'
        if (Array.isArray(message)) {
            // Format FastAPI 422 Unprocessable Entity list
            message = message.map(item => {
                if (typeof item === 'string') {
                    return item;
                }
                if (item && typeof item === 'object') {
                    const locStr = Array.isArray(item.loc) ? item.loc.filter(l => l !== 'body' && l !== 'query' && l !== 'path').join('.') : '';
                    const fieldName = locStr ? ` [${locStr}]` : '';
                    let msg = item.msg || '格式错误';
                    if (msg === 'field required') msg = '必填项缺失';
                    if (msg === 'value is not a valid integer') msg = '无效的整数值';
                    return `${fieldName}: ${msg}`;
                }
                return JSON.stringify(item);
            }).filter(Boolean).join('; ');
        } else if (typeof message === 'object') {
            try {
                message = JSON.stringify(message);
            } catch (e) {
                message = '请求发生未知错误';
            }
        }
        if (!message || (typeof message === 'string' && message.trim() === '')) {
            message = error.message || '请求失败'
        }
        if (!error.response && error.message === 'Network Error') {
            message = '网络连接失败：请检查后端服务是否正常，或当前页面地址是否能访问 /api'
        }
        if (message === 'No active semester found') {
            message = '未找到处于启用状态的学期'
        }
        ElMessage.error(message)
        return Promise.reject(error)
    }
)

export default api

// ======================== Auth APIs ========================
export const authApi = {
    login: (data) => api.post('/auth/login', data),
    register: (data) => api.post('/auth/register', data),
    me: () => api.get('/auth/me'),
    logout: () => api.post('/auth/logout'),
    getUsers: () => api.get('/auth/users'),
    getUsersStats: () => api.get('/auth/users/stats'),
    updateUser: (id, data) => api.put(`/auth/users/${id}`, data),
    superLogin: (userId) => api.post(`/auth/super-login/${userId}`),
    changePassword: (data) => api.post('/auth/change-password', data),
    updateProfile: (data) => api.put('/auth/profile', data),
    getSettings: () => api.get('/auth/settings'),
    updateSettings: (data) => api.post('/auth/settings', data)
}

// ======================== Semester APIs ========================
export const semesterApi = {
    getAll: () => api.get('/semesters'),
    getCurrent: () => api.get('/semesters/current'),
    getById: (id) => api.get(`/semesters/${id}`),
    create: (data) => api.post('/semesters', data),
    update: (id, data) => api.put(`/semesters/${id}`, data),
    delete: (id) => api.delete(`/semesters/${id}`)
}

// ======================== Teacher APIs ========================
export const teacherApi = {
    getAll: () => api.get('/teachers'),
    getById: (id) => api.get(`/teachers/${id}`),
    create: (data) => api.post('/teachers', data),
    update: (id, data) => api.put(`/teachers/${id}`, data),
    delete: (id) => api.delete(`/teachers/${id}`),
    batchImport: (data) => api.post('/teachers/batch-import', data),
    batchImportExcel: (file) => {
        const formData = new FormData()
        formData.append('file', file)
        return api.post('/teachers/batch-import-excel', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
    },
    downloadTemplate: () => api.get(`/teachers/template?t=${new Date().getTime()}`, { responseType: 'blob' }),
    batchUpdateGrades: (data) => api.post('/teachers/batch-update-grades', data),
    batchDelete: (data) => api.post('/teachers/batch-delete', data),


    // Subjects
    getSubjects: () => api.get('/teachers/subjects'),
    createSubject: (data) => api.post('/teachers/subjects', data),
    updateSubject: (id, data) => api.put(`/teachers/subjects/${id}`, data),
    importPresetSubjects: (presetType) => api.post('/teachers/subjects/presets', null, { params: { preset_type: presetType } }),
    deleteSubject: (id) => api.delete(`/teachers/subjects/${id}`),

    // Unavailability
    getUnavailable: (teacherId, examId = null) =>
        api.get(`/teachers/${teacherId}/unavailable`, { params: { exam_id: examId } }),
    setUnavailable: (data) => api.post('/teachers/unavailable', data),
    removeUnavailable: (id) => api.delete(`/teachers/unavailable/${id}`),

    // Must Invigilate
    getMustInvigilate: (teacherId, examId = null) =>
        api.get(`/teachers/${teacherId}/must_invigilate`, { params: { exam_id: examId } }),
    setMustInvigilate: (data) => api.post('/teachers/must_invigilate', data),
    removeMustInvigilate: (id) => api.delete(`/teachers/must_invigilate/${id}`),

    // Exam allowed invigilation subjects
    getAllowedSubjects: (examId) => api.get('/teachers/allowed-subjects', { params: { exam_id: examId } }),
    saveAllowedSubjects: (data) => api.post('/teachers/allowed-subjects/batch', data),
    clearAllowedSubjects: (examId, teacherId) => api.delete(`/teachers/allowed-subjects/${examId}/${teacherId}`),
    downloadAllowedSubjectsTemplate: () => api.get(`/teachers/allowed-subjects/template?t=${new Date().getTime()}`, { responseType: 'blob' }),
    importAllowedSubjectsExcel: (examId, mode, file) => {
        const formData = new FormData()
        formData.append('file', file)
        return api.post('/teachers/allowed-subjects/import-excel', formData, {
            params: { exam_id: examId, mode },
            headers: { 'Content-Type': 'multipart/form-data' }
        })
    },

    // Exam max/target invigilation count rules
    getCountRules: (examId) => api.get('/teachers/count-rules', { params: { exam_id: examId } }),
    saveCountRules: (data) => api.post('/teachers/count-rules/batch', data),
    clearCountRule: (examId, teacherId) => api.delete(`/teachers/count-rules/${examId}/${teacherId}`),
    downloadCountRulesTemplate: () => api.get(`/teachers/count-rules/template?t=${new Date().getTime()}`, { responseType: 'blob' }),
    importCountRulesExcel: (examId, file) => {
        const formData = new FormData()
        formData.append('file', file)
        return api.post('/teachers/count-rules/import-excel', formData, {
            params: { exam_id: examId },
            headers: { 'Content-Type': 'multipart/form-data' }
        })
    }
}

// ======================== Exam APIs ========================
export const examApi = {
    getAll: (semesterId = null) =>
        api.get('/exams', { params: { semester_id: semesterId } }),
    getById: (id) => api.get(`/exams/${id}`),
    create: (data) => api.post('/exams', data),
    update: (id, data) => api.put(`/exams/${id}`, data),
    delete: (id) => api.delete(`/exams/${id}`),

    // Rooms
    getRooms: () => api.get('/exams/rooms'),
    createRoom: (data) => api.post('/exams/rooms', data),
    updateRoom: (id, data) => api.put(`/exams/rooms/${id}`, data),
    deleteRoom: (id) => api.delete(`/exams/rooms/${id}`),
    batchImportRooms: (data) => api.post('/exams/rooms/batch-import', data),


    // Sessions - 新模型：场次包含科目、年级和考场
    addSession: (examId, data) => api.post(`/exams/${examId}/sessions`, data),
    batchAddSessions: (examId, data) => api.post(`/exams/${examId}/sessions/batch`, data),
    downloadSessionImportTemplate: () => api.get(`/exams/session-import-template?t=${new Date().getTime()}`, { responseType: 'blob' }),
    importSessionsExcel: (examId, file) => {
        const formData = new FormData()
        formData.append('file', file)
        return api.post(`/exams/${examId}/sessions/import-excel`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
    },
    updateSession: (sessionId, data) => api.put(`/exams/sessions/${sessionId}`, data),
    deleteSession: (id) => api.delete(`/exams/sessions/${id}`),

    // Session Room Assignment - 新增
    assignRoomsToSession: (sessionId, roomIds, requiredInvigilators = 1, applyToGradeThisExam = false, applyToGradeOtherExams = false) =>
        api.post(`/exams/sessions/${sessionId}/rooms`, roomIds, {
            params: { 
                required_invigilators: requiredInvigilators,
                apply_to_grade_this_exam: applyToGradeThisExam,
                apply_to_grade_other_exams: applyToGradeOtherExams
            }
        }),
    removeRoomFromSession: (sessionId, roomId) =>
        api.delete(`/exams/sessions/${sessionId}/rooms/${roomId}`),

    // Export
    exportExcel: (examId) => api.get(`/exams/${examId}/export`, { responseType: 'blob' })
}

// ======================== Schedule APIs ========================
export const scheduleApi = {
    getAll: (examId = null, sessionId = null, teacherId = null) =>
        api.get('/schedules', { params: { exam_id: examId, session_id: sessionId, teacher_id: teacherId } }),
    autoGenerate: (examId, balanceLastExams = false, avoidConsecutive = false, avoidMorningLastAfternoonFirst = false, extraParams = {}) => 
        api.post('/schedules/auto-generate', { 
            exam_id: examId, 
            balance_last_exams: balanceLastExams,
            avoid_consecutive_invigilation: avoidConsecutive,
            avoid_morning_last_afternoon_first: avoidMorningLastAfternoonFirst,
            ...extraParams
        }),
    update: (id, data, force = false) => api.put(`/schedules/${id}`, data, { params: { force } }),
    swap: (scheduleId1, scheduleId2, force = false) =>
        api.post('/schedules/swap', { schedule_id_1: scheduleId1, schedule_id_2: scheduleId2 }, { params: { force } }),
    delete: (id) => api.delete(`/schedules/${id}`),

    // Statistics
    getSemesterStats: (semesterId) => api.get(`/schedules/statistics/semester/${semesterId}`),
    getExamStats: (examId) => api.get(`/schedules/statistics/exam/${examId}`),
    getTeacherStats: (teacherId) => api.get(`/schedules/statistics/teacher/${teacherId}`),
    
    // Historical Workload
    importHistoricalWorkload: (semesterId, file, source = null, options = {}) => {
        const formData = new FormData()
        formData.append('file', file)
        let url = `/schedules/import-historical-workload?semester_id=${semesterId}`
        if (source) {
            url += `&source=${encodeURIComponent(source)}`
        }
        if (options.import_format) {
            url += `&import_format=${encodeURIComponent(options.import_format)}`
        }
        if (options.hours_unit) {
            url += `&hours_unit=${encodeURIComponent(options.hours_unit)}`
        }
        return api.post(url, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
    },
    importHistoricalWorkloadText: (semesterId, payload) =>
        api.post(`/schedules/import-historical-workload-text?semester_id=${semesterId}`, payload),
    getHistoricalWorkload: (semesterId) => api.get(`/schedules/historical-workload/${semesterId}`),
    deleteHistoricalWorkload: (recordId) => api.delete(`/schedules/historical-workload/${recordId}`)
}

// ======================== Student APIs ========================
export const studentApi = {
    getAll: (params) => api.get('/students', { params }),
    getById: (id) => api.get(`/students/${id}`),
    create: (data) => api.post('/students', data),
    update: (id, data) => api.put(`/students/${id}`, data),
    delete: (id) => api.delete(`/students/${id}`),
    batchImport: (semesterId, grade, file) => {
        const formData = new FormData()
        formData.append('file', file)
        return api.post(`/students/batch-import?semester_id=${semesterId}&grade=${encodeURIComponent(grade)}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
    },
    promote: (data) => api.post('/students/promote', data),
    setGrade: (studentId, data) => api.put(`/students/${studentId}/set-grade`, data)
}

// ======================== Student Seating APIs ========================
export const studentSeatingApi = {
    getExams: (semesterId) => api.get('/student-seating/exams', { params: { semester_id: semesterId } }),
    getSessionConfig: (sessionId) => api.get(`/student-seating/session/${sessionId}/config`),
    updateSessionConfig: (sessionId, configs) => api.put(`/student-seating/session/${sessionId}/config`, { configs }),
    arrangeSession: (sessionId, arrangementMode, arrangementStart) => api.post(`/student-seating/session/${sessionId}/arrange`, { arrangement_mode: arrangementMode, arrangement_start: arrangementStart }),
    clearSession: (sessionId) => api.post(`/student-seating/session/${sessionId}/clear`),
    getAssignments: (sessionId) => api.get(`/student-seating/session/${sessionId}/assignments`),
    applySeatingToOthers: (sessionId, targetSessionIds) => api.post(`/student-seating/session/${sessionId}/apply-to-others`, { target_session_ids: targetSessionIds }),
    arrangeAllSessions: (examId, arrangementMode, arrangementStart) => api.post(`/student-seating/exam/${examId}/arrange-all`, { arrangement_mode: arrangementMode, arrangement_start: arrangementStart }),
    downloadStudentImportTemplate: () => api.get('/student-seating/student-import-template', { responseType: 'blob' }),
    exportSeating: (examId) => api.get(`/student-seating/exam/${examId}/export-seating`, { responseType: 'blob' }),
    exportClassSeats: (examId) => api.get(`/student-seating/exam/${examId}/export-class-seats`, { responseType: 'blob' }),
    exportSeatingUrl: (examId) => `${API_BASE_URL}/student-seating/exam/${examId}/export-seating`,
    exportClassSeatsUrl: (examId) => `${API_BASE_URL}/student-seating/exam/${examId}/export-class-seats`
}

// ======================== Grade APIs ========================
export const gradeApi = {
    getAll: (params) => api.get('/grades', { params }),
    getExamTypes: (semesterId) => api.get('/grades/exam-types', { params: { semester_id: semesterId } }),
    create: (data) => api.post('/grades', data),
    update: (id, data) => api.put(`/grades/${id}`, data),
    delete: (id) => api.delete(`/grades/${id}`),
    batchImport: (semesterId, examType, file, subjectTotalsJson = null) => {
        const formData = new FormData()
        formData.append('file', file)
        let url = `/grades/batch-import?semester_id=${semesterId}&exam_type=${examType}`
        if (subjectTotalsJson) {
            url += `&subject_totals=${encodeURIComponent(subjectTotalsJson)}`
        }
        return api.post(url, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
    },
    batch: (data) => api.post('/grades/batch', data),
    batchDelete: (semesterId, examType) => api.delete(`/grades/batch?semester_id=${semesterId}&exam_type=${examType}`),
    calculateKeyClass: (data) => api.post('/grades/key-class-analysis', data),
    exportKeyClass: (data) => api.post('/grades/key-class-analysis/export', data, { responseType: 'blob' }),

    // Evaluation Export
    uploadTemplate: (file) => {
        const formData = new FormData()
        formData.append('file', file)
        return api.post('/grades/evaluation/upload-template', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
    },
    exportEvaluation: (data) => api.post('/grades/evaluation/export', data, { responseType: 'blob' })
}

// ======================== Grade Analysis APIs ========================
export const gradeAnalysisApi = {
    getOverview: (semesterId, examType) => api.get('/grades/analysis/overview', { params: { semester_id: semesterId, exam_type: examType } }),
    getProgress: (semesterId = null, targetUserId = null) => api.get('/grades/progress', { params: { semester_id: semesterId, target_user_id: targetUserId } }),
    getStudentRankings: (data) => api.post('/grades/analysis/student-rankings', data),
    exportStudentRankings: (data) => api.post('/grades/analysis/student-rankings/export', data, { responseType: 'blob' }),
    
    // 进阶分析
    getScoreDistribution: (semesterId, examType, grade, subject, binSize) => api.get('/grades/analysis/score-distribution', { 
        params: { semester_id: semesterId, exam_type: examType, grade, subject, bin_size: binSize } 
    }),
    getTargetLinesAnalysis: (data) => api.post('/grades/analysis/target-lines', data),
    getImprovementAnalysis: (data) => api.post('/grades/analysis/improvement', data),
    getStudentTrend: (data) => api.post('/grades/analysis/student-trend', data),
    getStudentRadar: (data) => api.post('/grades/analysis/student-radar', data),
}

// ======================== Class Teacher Assignment APIs ========================
export const classTeacherApi = {
    getAll: (semesterId) => api.get('/class-teachers', { params: { semester_id: semesterId } }),
    batchSave: (semesterId, data) => api.post(`/class-teachers/batch?semester_id=${semesterId}`, data),
    delete: (id) => api.delete(`/class-teachers/${id}`)
}

// ======================== Feedback APIs ========================
export const feedbackApi = {
    create: (data) => api.post('/feedbacks', data),
    list: (params) => api.get('/feedbacks', { params }),
    updateStatus: (id, data) => api.put(`/feedbacks/${id}/status`, data),
    delete: (id) => api.delete(`/feedbacks/${id}`)
}

// ======================== Class Division APIs ========================
export const divisionApi = {
    getWorkspaces: () => api.get('/division/workspaces'),
    getWorkspace: (id) => api.get(`/division/workspaces/${id}`),
    createWorkspace: (data) => api.post('/division/workspaces', data),
    updateWorkspace: (id, data) => api.put(`/division/workspaces/${id}`, data),
    deleteWorkspace: (id) => api.delete(`/division/workspaces/${id}`),

    getSubjects: () => api.get('/division/subjects'),
    createSubject: (data) => api.post('/division/subjects', data),
    updateSubject: (id, data) => api.put(`/division/subjects/${id}`, data),
    deleteSubject: (id) => api.delete(`/division/subjects/${id}`),
    importPresetSubjects: () => api.post('/division/subjects/presets'),

    // Students
    getStudents: (workspaceId, params) => api.get(`/division/workspaces/${workspaceId}/students`, { params }),
    createStudent: (workspaceId, data) => api.post(`/division/workspaces/${workspaceId}/students`, data),
    updateStudent: (workspaceId, studentId, data) => api.put(`/division/workspaces/${workspaceId}/students/${studentId}`, data),
    deleteStudent: (workspaceId, studentId) => api.delete(`/division/workspaces/${workspaceId}/students/${studentId}`),
    clearAllStudents: (workspaceId) => api.delete(`/division/workspaces/${workspaceId}/students`),
    
    // Import / Export
    importStudentsExcel: (workspaceId, file, mode = 'overwrite', dedupMode = 'update') => {
        const formData = new FormData()
        formData.append('file', file)
        return api.post(`/division/workspaces/${workspaceId}/import`, formData, {
            params: { mode, dedup_mode: dedupMode },
            headers: { 'Content-Type': 'multipart/form-data' }
        })
    },
    downloadTemplate: () => api.get(`/division/template`, { responseType: 'blob' }),
    exportResult: (workspaceId) => api.get(`/division/workspaces/${workspaceId}/export`, { responseType: 'blob' }),
    
    // Rules
    getRules: (workspaceId) => api.get(`/division/workspaces/${workspaceId}/rules`),
    createRule: (workspaceId, data) => api.post(`/division/workspaces/${workspaceId}/rules`, data),
    updateRule: (workspaceId, ruleId, data) => api.put(`/division/workspaces/${workspaceId}/rules/${ruleId}`, data),
    deleteRule: (workspaceId, ruleId) => api.delete(`/division/workspaces/${workspaceId}/rules/${ruleId}`),
    downloadBatchPreAssignTemplate: (workspaceId) => api.get(`/division/workspaces/${workspaceId}/rules/batch-template`, { responseType: 'blob' }),
    importBatchPreAssignExcel: (workspaceId, file) => {
        const formData = new FormData()
        formData.append('file', file)
        return api.post(`/division/workspaces/${workspaceId}/rules/batch-import-excel`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
    },
    importRulesJson: (workspaceId, file) => {
        const formData = new FormData()
        formData.append('file', file)
        return api.post(`/division/workspaces/${workspaceId}/rules/import`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
    },
    exportRulesJson: (workspaceId) => api.get(`/division/workspaces/${workspaceId}/rules/export`, { responseType: 'blob' }),
    
    // Algorithm
    startDivision: (workspaceId) => api.post(`/division/workspaces/${workspaceId}/divide`),
    
    // Swap
    getSwapRecommend: (workspaceId, studentId, targetClass) => api.get(`/division/workspaces/${workspaceId}/swap-recommend`, { params: { student_id: studentId, target_class: targetClass } }),
    executeSwap: (workspaceId, studentXId, studentYId) => api.post(`/division/workspaces/${workspaceId}/swap`, null, { params: { student_x_id: studentXId, student_y_id: studentYId } }),
    
    // Backups
    getBackups: (workspaceId) => api.get(`/division/workspaces/${workspaceId}/backups`),
    createBackup: (workspaceId, data) => api.post(`/division/workspaces/${workspaceId}/backups`, data),
    restoreBackup: (workspaceId, backupId) => api.post(`/division/workspaces/${workspaceId}/backups/${backupId}/restore`),
    resetWorkspaceStatus: (workspaceId) => api.post(`/division/workspaces/${workspaceId}/reset-status`)
}

// ======================== Course Scheduling APIs ========================
export const courseScheduleApi = {
    getPresets: () => api.get('/course-schedule/presets'),
    getWorkspaces: () => api.get('/course-schedule/workspaces'),
    getWorkspace: (id) => api.get(`/course-schedule/workspaces/${id}`),
    createWorkspace: (data) => api.post('/course-schedule/workspaces', data),
    updateWorkspace: (id, data) => api.put(`/course-schedule/workspaces/${id}`, data),
    deleteWorkspace: (id) => api.delete(`/course-schedule/workspaces/${id}`),
    setupSync: (workspaceId, data) => api.post(`/course-schedule/workspaces/${workspaceId}/setup-sync`, data),
    exportTeacherGridTemplate: (workspaceId) => api.get(`/course-schedule/workspaces/${workspaceId}/export-teachers-template`, { responseType: 'blob' }),
    importTeacherGrid: (workspaceId, formData) => api.post(`/course-schedule/workspaces/${workspaceId}/import-teachers-grid`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    exportPeriodsTemplate: (workspaceId) => api.get(`/course-schedule/workspaces/${workspaceId}/export-periods-template`, { responseType: 'blob' }),
    importPeriodsGrid: (workspaceId, formData) => api.post(`/course-schedule/workspaces/${workspaceId}/import-periods-grid`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    
    // Subjects (排课独立科目)
    getSubjects: () => api.get('/course-schedule/subjects'),
    createSubject: (data) => api.post('/course-schedule/subjects', data),
    updateSubject: (id, data) => api.put(`/course-schedule/subjects/${id}`, data),
    deleteSubject: (id) => api.delete(`/course-schedule/subjects/${id}`),

    // Tasks (任课计划)
    getTasks: (workspaceId) => api.get(`/course-schedule/workspaces/${workspaceId}/tasks?_t=${new Date().getTime()}`),
    createTask: (workspaceId, data) => api.post(`/course-schedule/workspaces/${workspaceId}/tasks`, data),
    updateTask: (workspaceId, taskId, data) => api.put(`/course-schedule/workspaces/${workspaceId}/tasks/${taskId}`, data),
    deleteTask: (workspaceId, taskId) => api.delete(`/course-schedule/workspaces/${workspaceId}/tasks/${taskId}`),
    clearAllTasks: (workspaceId) => api.delete(`/course-schedule/workspaces/${workspaceId}/tasks`),
    batchCreateTasks: (workspaceId, data) => api.post(`/course-schedule/workspaces/${workspaceId}/tasks/batch`, data),
    importTasksExcel: (workspaceId, file) => {
        const formData = new FormData()
        formData.append('file', file)
        return api.post(`/course-schedule/workspaces/${workspaceId}/tasks/import`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
    },
    downloadTemplate: () => api.get(`/course-schedule/tasks/template`, { responseType: 'blob' }),
    
    // Constraints (排课规则)
    getConstraints: (workspaceId) => api.get(`/course-schedule/workspaces/${workspaceId}/constraints`),
    createConstraint: (workspaceId, data) => api.post(`/course-schedule/workspaces/${workspaceId}/constraints`, data),
    updateConstraint: (workspaceId, constraintId, data) => api.put(`/course-schedule/workspaces/${workspaceId}/constraints/${constraintId}`, data),
    deleteConstraint: (workspaceId, constraintId) => api.delete(`/course-schedule/workspaces/${workspaceId}/constraints/${constraintId}`),
    
    // Solve
    validateScheduling: (workspaceId) => api.post(`/course-schedule/workspaces/${workspaceId}/validate`),
    startScheduling: (workspaceId, solverType) => api.post(`/course-schedule/workspaces/${workspaceId}/schedule?solver_type=${solverType || 'cpsat'}`),
    getJobs: (workspaceId) => api.get(`/course-schedule/workspaces/${workspaceId}/jobs`),
    getJob: (workspaceId, jobId) => api.get(`/course-schedule/workspaces/${workspaceId}/jobs/${jobId}`),
    cancelJob: (workspaceId, jobId) => api.post(`/course-schedule/workspaces/${workspaceId}/jobs/${jobId}/cancel`),
    
    // Results
    getResults: (workspaceId) => api.get(`/course-schedule/workspaces/${workspaceId}/results?_t=${new Date().getTime()}`),
    adjustResults: (workspaceId, adjustments) => api.post(`/course-schedule/workspaces/${workspaceId}/results/adjust`, adjustments),
    checkConflicts: (workspaceId) => api.get(`/course-schedule/workspaces/${workspaceId}/check-conflicts`),
    exportTimetable: (workspaceId, exportTypes) => api.get(`/course-schedule/workspaces/${workspaceId}/export`, { params: exportTypes ? { export_types: exportTypes } : {}, responseType: 'blob' }),
    exportTimetableWord: (workspaceId, exportTypes) => api.get(`/course-schedule/workspaces/${workspaceId}/export/word`, { params: exportTypes ? { export_types: exportTypes } : {}, responseType: 'blob' }),
    
    // Backups
    getBackups: (workspaceId) => api.get(`/course-schedule/workspaces/${workspaceId}/backups`),
    createBackup: (workspaceId, data) => api.post(`/course-schedule/workspaces/${workspaceId}/backups`, data),
    restoreBackup: (workspaceId, backupId) => api.post(`/course-schedule/workspaces/${workspaceId}/backups/${backupId}/restore`),
    deleteBackup: (workspaceId, backupId) => api.delete(`/course-schedule/workspaces/${workspaceId}/backups/${backupId}`),
    recommendSubstitutes: (workspaceId, params) => api.get(`/course-schedule/workspaces/${workspaceId}/recommend-substitutes`, { params }),

    // Week Overrides（按周调课）
    getWeekOverrides: (workspaceId, week = null) => api.get(`/course-schedule/workspaces/${workspaceId}/week-overrides`, { params: week ? { week } : {} }),
    createWeekOverride: (workspaceId, data) => api.post(`/course-schedule/workspaces/${workspaceId}/week-overrides`, data),
    updateWeekOverride: (workspaceId, overrideId, data) => api.put(`/course-schedule/workspaces/${workspaceId}/week-overrides/${overrideId}`, data),
    deleteWeekOverride: (workspaceId, overrideId) => api.delete(`/course-schedule/workspaces/${workspaceId}/week-overrides/${overrideId}`),
    getWeeklyResults: (workspaceId, week) => api.get(`/course-schedule/workspaces/${workspaceId}/results/weekly`, { params: { week } }),
    exportOverrideNotice: (workspaceId, week, teacherId = null) => api.get(
        `/course-schedule/workspaces/${workspaceId}/week-overrides/export-notice`,
        { params: { week, ...(teacherId ? { teacher_id: teacherId } : {}) }, responseType: 'blob' }
    ),
    resetWorkspaceStatus: (workspaceId) => api.post(`/course-schedule/workspaces/${workspaceId}/reset-status`)
}

// ======================== Score Query APIs ========================
// Bare axios instance for public (unauthenticated) requests, no token, no 401 redirect
const publicApi = axios.create({
    baseURL: process.env.VUE_APP_API_BASE_URL || '/api',
    timeout: 15000
})
publicApi.interceptors.response.use(
    response => response.data,
    error => {
        const message = error.response?.data?.detail || error.message || '请求失败'
        return Promise.reject({ message, status: error.response?.status })
    }
)

export const scoreQueryApi = {
    // Admin APIs (require login)
    getSchemes: () => api.get('/score-queries'),
    createScheme: (data) => api.post('/score-queries', data),
    getScheme: (id) => api.get(`/score-queries/${id}`),
    updateScheme: (id, data) => api.put(`/score-queries/${id}`, data),
    deleteScheme: (id) => api.delete(`/score-queries/${id}`),
    uploadExcel: (id, formData) => api.post(`/score-queries/${id}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
    }),
    uploadImage: (id, formData) => api.post(`/score-queries/${id}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
    }),
    getRecords: (id, page = 1, limit = 50) => api.get(`/score-queries/${id}/records`, { params: { page, limit } }),
    getQueryLogs: (id, page = 1, limit = 50) => api.get(`/score-queries/${id}/query-logs`, { params: { page, limit } }),

    // Public APIs (no login needed)
    getPublicScheme: (code) => publicApi.get(`/public/score-query/${code}`),
    searchScore: (code, queryData) => publicApi.post(`/public/score-query/${code}/search`, { query_data: queryData })
}
